import { Box, List, ListItem, Stack, Typography, Chip } from "@mui/material";
import PropTypes from "prop-types";
import LockOpenIcon from "@mui/icons-material/LockOpen";

import theme from "../../theme";
import { useSelector } from "react-redux";

function SelectPlanCard({ data, isSelected, onClick, isUnlocked = false }) {
  const { id, name, cost, features, clients, users, processing_fee } = data;

  const {
    currentPlan: { planId },
  } = useSelector((state) => state.settings);

  const isCurrentPlan = planId === id;

  return (
    <Box
      onClick={onClick}
      sx={{
        backgroundColor: isSelected
          ? theme.palette.primary.main
          : isUnlocked
            ? "rgba(255, 193, 7, 0.05)"
            : theme.palette.white.light,
        cursor: "pointer",
        color: isSelected ? theme.palette.common.white : theme.palette.common.black,
        width: "100%",
        maxWidth: "400px",
        margin: "auto",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        borderRadius: "12px",
        border: isUnlocked ? "2px solid #ff9800" : "none",
        position: "relative",
        boxShadow: isSelected || isUnlocked ? "0px 4px 12px rgba(0, 0, 0, 0.15)" : "none",
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          transform: "translateY(-2px)",
        },
      }}
    >
      {/* NEW: Unlocked Badge */}
      {isUnlocked && (
        <Chip
          icon={<LockOpenIcon sx={{ fontSize: 16 }} />}
          label="Unlocked!"
          color="warning"
          size="small"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            fontWeight: 600,
            zIndex: 1,
            animation: "pulse 2s infinite",
            "@keyframes pulse": {
              "0%, 100%": { opacity: 1 },
              "50%": { opacity: 0.7 },
            },
          }}
        />
      )}

      <Stack spacing="16px" paddingX="18px" paddingY="24px">
        <Stack spacing={1}>
          <Typography
            variant="h6"
            component="h1"
            textAlign="center"
            fontWeight="700"
            fontSize={{ xs: "24px", sm: "28px", md: "32px", lg: "36px" }}
          >
            {name} {isCurrentPlan && "(Current)"}
          </Typography>
          <Typography
            variant="subtitle2"
            color={isSelected ? "white" : theme.palette.primary.main}
            fontWeight="700"
            fontSize="20px"
            textAlign="center"
          >
            {cost}
          </Typography>
        </Stack>
        <Box paddingX="12px" paddingY="8px" sx={{ overflowY: "auto" }}>
          <List sx={{ listStyleType: "disc", paddingLeft: "10px" }} disablePadding>
            <ListItem
              disablePadding
              sx={{
                display: "list-item",
                paddingLeft: "0px",
                fontSize: { xs: "14px", sm: "16px" },
              }}
            >
              <Typography>Access to the following features:</Typography>
              <List sx={{ listStyleType: "disc", paddingLeft: "20px" }} disablePadding>
                {features.map((feature, index) => (
                  <ListItem
                    disablePadding
                    key={index}
                    sx={{
                      display: "list-item",
                      fontSize: { xs: "14px", sm: "16px" },
                    }}
                  >
                    <Typography>{feature}</Typography>
                  </ListItem>
                ))}
              </List>
            </ListItem>
          </List>
        </Box>

        <Box paddingX="12px" paddingY="8px" sx={{ overflowY: "auto" }}>
          <List sx={{ listStyleType: "disc", paddingLeft: "10px" }} disablePadding>
            <ListItem
              sx={{
                display: "list-item",
                paddingLeft: "0px",
                fontSize: { xs: "14px", sm: "16px" },
              }}
            >
              <Typography>{clients}</Typography>
            </ListItem>
            <ListItem
              sx={{
                display: "list-item",
                paddingLeft: "0px",
                fontSize: { xs: "14px", sm: "16px" },
              }}
            >
              <Typography>{users}</Typography>
            </ListItem>
            {processing_fee && (
              <ListItem
                sx={{
                  display: "list-item",
                  paddingLeft: "0px",
                  fontSize: { xs: "14px", sm: "16px" },
                }}
              >
                <Typography>{processing_fee} processing fee on payments</Typography>
              </ListItem>
            )}
          </List>
        </Box>
      </Stack>
    </Box>
  );
}

SelectPlanCard.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    cost: PropTypes.string.isRequired,
    features: PropTypes.arrayOf(PropTypes.string).isRequired,
    clients: PropTypes.string.isRequired,
    users: PropTypes.string.isRequired,
    processing_fee: PropTypes.string,
  }).isRequired,
  isSelected: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  isUnlocked: PropTypes.bool,
};

export default SelectPlanCard;
